import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios, { AxiosError } from "axios";
import { handleApiError } from "@/utils/apiErrorHandler";
import { clearError } from "./errorSlice";


interface ReportTypeQuery {
  groupId: number;
  premiumSpecialty?: string;
  isDesignated?: boolean;
  version?: number;
  isModal?: boolean;
  eligible?: boolean;
}

// Helper function to build query string (matching style of buildQueryString in resultsByPhysicianSlice.ts)
function toQueryString(query: ReportTypeQuery) {
  const params = new URLSearchParams();
  if (query.groupId !== undefined)
    params.append("groupID", String(query.groupId));
  if (query.premiumSpecialty)
    params.append("premiumSpecialty", query.premiumSpecialty);
  params.append("isDesignated", "true");
  params.append("version", "17");
  params.append("isModal", "true");
  params.append("eligible", "true");
  return params.toString();
}

export const fetchReportTypes = createAsyncThunk(
  "reportsSlice/fetchReportTypes",
  async ({ query }: { query: ReportTypeQuery }, { getState, dispatch, rejectWithValue }) => {
    try {
      dispatch(clearError());
      const queryString = toQueryString(query);
      const response = await axios.get(`/papi/secure/groups/reportTypes?${queryString}`);
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError, "reports", dispatch);
      return rejectWithValue(error instanceof Error ? error.message : "Error fetching group report types");
    }
  }
);

const initialState = {
  reportTypes: [],
  loading: false,
  error: null as string | null,
};
const reportsSlice = createSlice({
  name: "groupSummaryReports",
  initialState,
  reducers: {
    clearReports: (state) => {
      state.reportTypes = [];
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReportTypes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReportTypes.fulfilled, (state, action) => {
        state.loading = false;
        state.reportTypes = action.payload.data;
      })
      .addCase(fetchReportTypes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? null;
      });
  },
});

export const { clearReports } = reportsSlice.actions;
export default reportsSlice.reducer;
